import { BOARD_SIZE, type BoardSquare, type PlacedTileDto, type Seat, type TileKind, type TileSide } from './schemas.js'
import { TILE_DEFINITIONS, selfPosition, type FaceGrid, type TileGridChar } from './duke-tiles.js'
import { legalPlacementSquares } from './duke-bag.js'

/**
 * The Duke's movement legality across the standard 15-kind roster — the
 * sibling of `duke-setup.ts`, same contract: pure functions, the single
 * implementation both the mock server (as referee, `server/duke-api.store.ts`)
 * and the client (as a highlighter of legal targets/options,
 * `features/game/play-board.presenter.ts`) share. No architecture test
 * governs `src/shared/`; keeping this the sole implementation is a
 * review-level discipline (see `duke-setup.ts`'s own note).
 *
 * The full icon vocabulary a grid cell can carry: Move, Slide, Jump, Jump
 * Slide, Command, Strike (rulebook p.4-5). Deliberately out of scope: Defense
 * and Dread icons, terrain, the Enhanced tiles (Oracle, Duchess) and their
 * abilities. See `duke-tiles.ts` for the per-kind grids this reads.
 */

export type MoveIcon = 'move' | 'slide' | 'jump' | 'jump-slide' | 'command' | 'strike'

/**
 * One line of a tile's movement grid, in *forward-positive* coordinates: a
 * positive `dRow` is one step toward the opponent's home row, negative is
 * one step back toward the controller's own — the grid always faces the
 * controlling player (rulebook p.2, "Orientation"), so this is the same
 * table for both seats. `forwardSign` converts to an absolute board `dRow`
 * for a given seat; `dCol` never needs converting because every grid in
 * `duke-tiles.ts` happens to be left/right-symmetric.
 */
export type MoveSpec = Readonly<{ dRow: number; dCol: number; icon: MoveIcon }>

const ICON_FOR_CHAR: Readonly<Record<Exclude<TileGridChar, '.' | '@'>, MoveIcon>> = {
  m: 'move',
  s: 'slide',
  j: 'jump',
  S: 'jump-slide',
  c: 'command',
  x: 'strike',
}

function moveSpecsForFace(face: FaceGrid): readonly MoveSpec[] {
  const self = selfPosition(face)
  const specs: MoveSpec[] = []
  for (let row = 0; row < 5; row++) {
    for (let col = 0; col < 5; col++) {
      const ch = face[row][col]
      if (ch === '.' || ch === '@') continue
      specs.push({
        dRow: self.row - row,
        dCol: col - self.col,
        icon: ICON_FOR_CHAR[ch as Exclude<TileGridChar, '.' | '@'>],
      })
    }
  }
  return specs
}

/** Derived once, at module load, from `duke-tiles.ts`'s per-kind face grids. */
export const TILE_MOVES: Readonly<Record<TileKind, Readonly<Record<TileSide, readonly MoveSpec[]>>>> =
  Object.fromEntries(
    TILE_DEFINITIONS.map((def) => [
      def.kind,
      {
        starting: moveSpecsForFace(def.starting),
        'non-starting': moveSpecsForFace(def['non-starting']),
      },
    ]),
  ) as Record<TileKind, Readonly<Record<TileSide, readonly MoveSpec[]>>>

/** Host's home row is the board's last row, so the host's forward is toward row 0; the guest's is the reverse. */
export function forwardSign(seat: Seat): -1 | 1 {
  return seat === 'host' ? -1 : 1
}

function onBoard(square: BoardSquare): boolean {
  return square.row >= 0 && square.row < BOARD_SIZE && square.col >= 0 && square.col < BOARD_SIZE
}

function sameSquare(a: BoardSquare, b: BoardSquare): boolean {
  return a.row === b.row && a.col === b.col
}

function pieceAt(board: readonly PlacedTileDto[], square: BoardSquare): PlacedTileDto | null {
  return board.find((tile) => tile.row === square.row && tile.col === square.col) ?? null
}

function opponentOf(seat: Seat): Seat {
  return seat === 'host' ? 'guest' : 'host'
}

/** Every on-board, non-friendly-occupied square strictly between `from` and `to` along a straight line. */
function squaresBetween(from: BoardSquare, to: BoardSquare): readonly BoardSquare[] {
  const dRow = Math.sign(to.row - from.row)
  const dCol = Math.sign(to.col - from.col)
  const squares: BoardSquare[] = []
  let cursor: BoardSquare = { row: from.row + dRow, col: from.col + dCol }
  while (!sameSquare(cursor, to)) {
    squares.push(cursor)
    cursor = { row: cursor.row + dRow, col: cursor.col + dCol }
  }
  return squares
}

/**
 * Every square `seat`'s tile at `from` could relocate to by its movement
 * grid alone (Move and Slide/Jump/Jump Slide icons only — Command and Strike
 * don't relocate the acting tile, so they are separate turn actions below).
 * Ignores whether landing there would leave the mover's own Duke capturable
 * — that is `legalMoves`'s job, built on top of this so the self-check has a
 * non-recursive base case to call for the opponent's own reply.
 */
function rawLegalTargets(board: readonly PlacedTileDto[], seat: Seat, from: BoardSquare): readonly BoardSquare[] {
  const tile = pieceAt(board, from)
  if (tile === null || tile.owner !== seat) return []

  const targets: BoardSquare[] = []
  for (const spec of TILE_MOVES[tile.kind][tile.side]) {
    const dRow = spec.dRow * forwardSign(seat)
    const dCol = spec.dCol

    if (spec.icon === 'move') {
      // "clear and straight path" (rulebook p.4): every square strictly
      // between `from` and the target must be empty.
      const target: BoardSquare = { row: from.row + dRow, col: from.col + dCol }
      if (!onBoard(target)) continue
      if (squaresBetween(from, target).some((sq) => pieceAt(board, sq) !== null)) continue
      const occupant = pieceAt(board, target)
      if (occupant !== null && occupant.owner === seat) continue
      targets.push(target)
      continue
    }

    if (spec.icon === 'slide') {
      // Step until the edge, a friendly tile (stop before it), or an enemy
      // tile (the last reachable square, since capturing it stops the slide).
      let cursor: BoardSquare = { row: from.row + dRow, col: from.col + dCol }
      while (onBoard(cursor)) {
        const occupant = pieceAt(board, cursor)
        if (occupant === null) {
          targets.push(cursor)
          cursor = { row: cursor.row + dRow, col: cursor.col + dCol }
          continue
        }
        if (occupant.owner !== seat) targets.push(cursor)
        break
      }
      continue
    }

    if (spec.icon === 'jump') {
      // Straight to the named square, ignoring anything in between; blocked
      // only by a friendly tile on the target itself.
      const target: BoardSquare = { row: from.row + dRow, col: from.col + dCol }
      if (!onBoard(target)) continue
      const occupant = pieceAt(board, target)
      if (occupant !== null && occupant.owner === seat) continue
      targets.push(target)
      continue
    }

    if (spec.icon === 'jump-slide') {
      // The grid marks a Jump Slide icon at some distance in the direction it
      // acts (rulebook p.4 diagram) — normalise to a unit step: jump the
      // immediately adjacent square unconditionally, then slide onward one
      // square at a time from there.
      const stepRow = Math.sign(dRow)
      const stepCol = Math.sign(dCol)
      const jumped: BoardSquare = { row: from.row + stepRow, col: from.col + stepCol }
      let cursor: BoardSquare = { row: jumped.row + stepRow, col: jumped.col + stepCol }
      while (onBoard(cursor)) {
        const occupant = pieceAt(board, cursor)
        if (occupant === null) {
          targets.push(cursor)
          cursor = { row: cursor.row + stepRow, col: cursor.col + stepCol }
          continue
        }
        if (occupant.owner !== seat) targets.push(cursor)
        break
      }
      continue
    }
    // 'command' and 'strike' icons never relocate the acting tile — see
    // commandOptions / strikeTargets.
  }
  return targets
}

/** `board` with `from`'s tile relocated to `to` (capturing whatever was there) and flipped to its other face. */
function simulateMove(
  board: readonly PlacedTileDto[],
  seat: Seat,
  from: BoardSquare,
  to: BoardSquare,
): readonly PlacedTileDto[] {
  const mover = pieceAt(board, from)
  if (mover === null) return board

  const flipped: TileSide = mover.side === 'starting' ? 'non-starting' : 'starting'
  return [
    ...board.filter((tile) => !sameSquare(tile, from) && !sameSquare(tile, to)),
    { row: to.row, col: to.col, kind: mover.kind, side: flipped, owner: seat },
  ]
}

/** `board` with the tile at `commander`'s Command icon moving a friendly tile from `from` to `to`, and only the commander flipped. */
function simulateCommand(
  board: readonly PlacedTileDto[],
  seat: Seat,
  commander: BoardSquare,
  from: BoardSquare,
  to: BoardSquare,
): readonly PlacedTileDto[] {
  const commanderTile = pieceAt(board, commander)
  const moved = pieceAt(board, from)
  if (commanderTile === null || moved === null) return board

  const flippedCommander: TileSide = commanderTile.side === 'starting' ? 'non-starting' : 'starting'
  return [
    ...board.filter((tile) => !sameSquare(tile, commander) && !sameSquare(tile, from) && !sameSquare(tile, to)),
    { row: commander.row, col: commander.col, kind: commanderTile.kind, side: flippedCommander, owner: seat },
    { row: to.row, col: to.col, kind: moved.kind, side: moved.side, owner: seat },
  ]
}

/** `board` with the striker's target captured and the striker itself flipped in place (it never moves — rulebook p.5). */
function simulateStrike(
  board: readonly PlacedTileDto[],
  seat: Seat,
  striker: BoardSquare,
  target: BoardSquare,
): readonly PlacedTileDto[] {
  const strikerTile = pieceAt(board, striker)
  if (strikerTile === null) return board

  const flipped: TileSide = strikerTile.side === 'starting' ? 'non-starting' : 'starting'
  return [
    ...board.filter((tile) => !sameSquare(tile, striker) && !sameSquare(tile, target)),
    { row: striker.row, col: striker.col, kind: strikerTile.kind, side: flipped, owner: seat },
  ]
}

/**
 * "A player cannot move a Troop Tile if it would allow the opponent to
 * capture his Duke [on their next turn]" (rulebook p.6) — the one rule
 * worth the extra pass, since without it the game is trivially broken.
 * `resultingBoard` is whatever the action (move, command or strike) produced.
 *
 * Exported as the same predicate a Duke sitting on the *current* board is
 * "in check" by (`resultingBoard` is then just `board` itself) — one rule,
 * two readings: a move filter here, a highlight for `PlayBoardPresenter` at
 * the call site that passes the unmodified board.
 */
export function isDukeInCheck(resultingBoard: readonly PlacedTileDto[], seat: Seat): boolean {
  const myDuke = resultingBoard.find((tile) => tile.owner === seat && tile.kind === 'duke')
  // No Duke of ours on the board is not this rule's problem to catch.
  if (myDuke === undefined) return false

  const opponent = opponentOf(seat)
  const target: BoardSquare = { row: myDuke.row, col: myDuke.col }
  return resultingBoard
    .filter((tile) => tile.owner === opponent)
    .some((enemyTile) => {
      const square: BoardSquare = { row: enemyTile.row, col: enemyTile.col }
      // A Command icon that could pull a friendly tile onto our Duke's
      // square is just as much a threat as relocating or striking there
      // directly (rulebook p.6's "capture his Duke" makes no exception for
      // how the capture is delivered) — but only when the opponent actually
      // has a friendly tile to command *from* one of this tile's Command
      // squares; `destinations` alone says where a Command could land, not
      // that one legally can right now. `commandOptions` is itself raw (no
      // `isDukeInCheck` call), so this stays non-recursive.
      const { sources, destinations } = commandOptions(resultingBoard, opponent, square)
      return (
        rawLegalTargets(resultingBoard, opponent, square).some((sq) => sameSquare(sq, target)) ||
        strikeTargets(resultingBoard, opponent, square).some((sq) => sameSquare(sq, target)) ||
        (sources.length > 0 && destinations.some((sq) => sameSquare(sq, target)))
      )
    })
}

/** Every square `seat` may legally move the tile at `from` to, self-check rule included. */
export function legalMoves(board: readonly PlacedTileDto[], seat: Seat, from: BoardSquare): readonly BoardSquare[] {
  return rawLegalTargets(board, seat, from).filter((to) => !isDukeInCheck(simulateMove(board, seat, from, to), seat))
}

/**
 * The Command icon's source/destination squares for the tile at `commander`
 * — a source must hold a friendly tile (the one to move), a destination must
 * be empty or hold an enemy (capturable), and moving into it must not expose
 * the mover's own Duke. Neither list is filtered against the other: picking
 * one square as source does not remove it from being offered as a
 * destination for a different pairing (rulebook p.4-5 places no such
 * restriction — a command move is validated as one (from, to) pair, in
 * `applyTurn`).
 */
export function commandOptions(
  board: readonly PlacedTileDto[],
  seat: Seat,
  commander: BoardSquare,
): Readonly<{ sources: readonly BoardSquare[]; destinations: readonly BoardSquare[] }> {
  const tile = pieceAt(board, commander)
  if (tile === null || tile.owner !== seat) return { sources: [], destinations: [] }

  const commandSquares: BoardSquare[] = []
  for (const spec of TILE_MOVES[tile.kind][tile.side]) {
    if (spec.icon !== 'command') continue
    const dRow = spec.dRow * forwardSign(seat)
    const square: BoardSquare = { row: commander.row + dRow, col: commander.col + spec.dCol }
    if (onBoard(square)) commandSquares.push(square)
  }

  const sources = commandSquares.filter((sq) => {
    const occupant = pieceAt(board, sq)
    return occupant !== null && occupant.owner === seat
  })
  const destinations = commandSquares.filter((sq) => {
    const occupant = pieceAt(board, sq)
    return occupant === null || occupant.owner !== seat
  })
  return { sources, destinations }
}

/** Every enemy-occupied square the Strike icon on the tile at `striker` can capture without moving. */
export function strikeTargets(
  board: readonly PlacedTileDto[],
  seat: Seat,
  striker: BoardSquare,
): readonly BoardSquare[] {
  const tile = pieceAt(board, striker)
  if (tile === null || tile.owner !== seat) return []

  const targets: BoardSquare[] = []
  for (const spec of TILE_MOVES[tile.kind][tile.side]) {
    if (spec.icon !== 'strike') continue
    const dRow = spec.dRow * forwardSign(seat)
    const square: BoardSquare = { row: striker.row + dRow, col: striker.col + spec.dCol }
    if (!onBoard(square)) continue
    const occupant = pieceAt(board, square)
    if (occupant !== null && occupant.owner !== seat) targets.push(square)
  }
  return targets
}

/**
 * Duke-adjacent empty squares (`duke-bag.ts`'s `legalPlacementSquares`) where
 * placing a newly drawn tile would not leave `seat`'s own Duke in check —
 * the same self-check filter `legalMoves` applies to a relocating Move. The
 * tile's `kind` can't change whether *our own* Duke ends up in check (only
 * which square becomes occupied does), so the default keeps a caller that
 * doesn't know the drawn kind yet honest; a real caller with a pending draw
 * passes it anyway for the reader's sake.
 */
export function legalDrawPlacements(
  board: readonly PlacedTileDto[],
  seat: Seat,
  kind: TileKind = 'footman',
): readonly BoardSquare[] {
  return legalPlacementSquares(board, seat).filter((square) => {
    const placed: readonly PlacedTileDto[] = [
      ...board,
      { row: square.row, col: square.col, kind, side: 'starting', owner: seat },
    ]
    return !isDukeInCheck(placed, seat)
  })
}

/** Whether `seat` could draw a tile from a bag of `bagCount` and place it legally right now — the fourth option `hasAnyLegalMove` doesn't see on its own. */
export function canDrawAndPlace(board: readonly PlacedTileDto[], seat: Seat, bagCount: number): boolean {
  return bagCount > 0 && legalDrawPlacements(board, seat).length > 0
}

/**
 * Whether `seat` has any legal move, command or strike at all, across every
 * tile they control. Unlike the physical game's bag-draw fallback, this app
 * *does* model drawing a new tile (`duke-bag.ts`), so a seat with an empty
 * hand of moves but a non-empty bag and a legal placement square is not
 * actually stuck — see `hasAnyLegalAction`, which adds that fourth option.
 */
export function hasAnyLegalMove(board: readonly PlacedTileDto[], seat: Seat): boolean {
  return board
    .filter((tile) => tile.owner === seat)
    .some((tile) => {
      const square: BoardSquare = { row: tile.row, col: tile.col }
      const { sources, destinations } = commandOptions(board, seat, square)
      // A Command or Strike is self-check filtered the same as a Move
      // (`legalMoves` above) — without this, a Command/Strike that would
      // expose the mover's own Duke still counted as "a move exists", which
      // could make a real checkmate read as a legal position with an escape.
      const hasLegalCommand = sources.some((from) =>
        destinations.some((to) => !isDukeInCheck(simulateCommand(board, seat, square, from, to), seat)),
      )
      const hasLegalStrike = strikeTargets(board, seat, square).some(
        (target) => !isDukeInCheck(simulateStrike(board, seat, square, target), seat),
      )
      return legalMoves(board, seat, square).length > 0 || hasLegalCommand || hasLegalStrike
    })
}

/** `hasAnyLegalMove` plus a fourth option: whether `seat` could legally draw-and-place from a bag of `bagCount`. */
export function hasAnyLegalAction(board: readonly PlacedTileDto[], seat: Seat, bagCount: number): boolean {
  return hasAnyLegalMove(board, seat) || canDrawAndPlace(board, seat, bagCount)
}

/**
 * Checkmate: the Duke is currently in check (`isDukeInCheck` on the
 * unmodified board) and `seat` has no move, Command, Strike or draw-and-place
 * that escapes it (`hasAnyLegalAction`, which is itself self-check filtered).
 */
export function isDukeInCheckmate(board: readonly PlacedTileDto[], seat: Seat, bagCount: number): boolean {
  return isDukeInCheck(board, seat) && !hasAnyLegalAction(board, seat, bagCount)
}

export type MoveRejection =
  | 'no-tile'
  | 'not-your-tile'
  | 'illegal-target'
  | 'duke-would-be-captured'
  | 'illegal-command'
  | 'illegal-strike'
  | 'bag-empty'
  | 'illegal-placement'
  | 'no-pending-draw'
  | 'draw-pending'

export type MoveResult =
  | { kind: 'ok'; board: readonly PlacedTileDto[]; captured: PlacedTileDto | null; winner: Seat | null }
  | { kind: 'rejected'; reason: MoveRejection }

function resultFor(
  resultingBoard: readonly PlacedTileDto[],
  seat: Seat,
  captured: PlacedTileDto | null,
  opponentBagCount: number,
): MoveResult {
  const opponent = opponentOf(seat)
  const winner =
    captured !== null && captured.kind === 'duke'
      ? seat
      : // Evaluated on `resultingBoard` — the board *after* this move/command/
        // strike landed — never the board before it, since the mover's own
        // action can be exactly what closes the opponent's last placement
        // square or frees up their blocker. Evaluating on the stale pre-move
        // board let a true checkmate go undeclared for one extra turn.
        !hasAnyLegalAction(resultingBoard, opponent, opponentBagCount)
        ? seat
        : null
  return { kind: 'ok', board: resultingBoard, captured, winner }
}

/**
 * The full legality check and effect for a single Move action. Whose turn it
 * actually is (`GameStatus`/`activeSeat`) is not this function's concern —
 * same split as `duke-setup.ts`'s `validateSetup`; the caller
 * (`server/duke-api.store.ts`) checks that first.
 */
export function applyMove(args: {
  board: readonly PlacedTileDto[]
  seat: Seat
  from: BoardSquare
  to: BoardSquare
  opponentBagCount?: number
}): MoveResult {
  const { board, seat, from, to, opponentBagCount = 0 } = args

  const tile = pieceAt(board, from)
  if (tile === null) return { kind: 'rejected', reason: 'no-tile' }
  if (tile.owner !== seat) return { kind: 'rejected', reason: 'not-your-tile' }

  const raw = rawLegalTargets(board, seat, from)
  const isRawLegal = raw.some((target) => sameSquare(target, to))
  if (!isRawLegal) return { kind: 'rejected', reason: 'illegal-target' }

  const resultingBoard = simulateMove(board, seat, from, to)
  if (isDukeInCheck(resultingBoard, seat)) {
    return { kind: 'rejected', reason: 'duke-would-be-captured' }
  }

  const captured = pieceAt(board, to)
  return resultFor(resultingBoard, seat, captured, opponentBagCount)
}

/**
 * The full legality check and effect for a single Command action: move the
 * tile at `from` to `to` using the Command icon on `commander`. Only
 * `commander` flips.
 */
export function applyCommand(args: {
  board: readonly PlacedTileDto[]
  seat: Seat
  commander: BoardSquare
  from: BoardSquare
  to: BoardSquare
  opponentBagCount?: number
}): MoveResult {
  const { board, seat, commander, from, to, opponentBagCount = 0 } = args

  const commanderTile = pieceAt(board, commander)
  if (commanderTile === null) return { kind: 'rejected', reason: 'no-tile' }
  if (commanderTile.owner !== seat) return { kind: 'rejected', reason: 'not-your-tile' }

  const { sources, destinations } = commandOptions(board, seat, commander)
  const isLegalSource = sources.some((sq) => sameSquare(sq, from))
  const isLegalDestination = destinations.some((sq) => sameSquare(sq, to))
  if (!isLegalSource || !isLegalDestination) return { kind: 'rejected', reason: 'illegal-command' }

  const resultingBoard = simulateCommand(board, seat, commander, from, to)
  if (isDukeInCheck(resultingBoard, seat)) {
    return { kind: 'rejected', reason: 'duke-would-be-captured' }
  }

  const captured = pieceAt(board, to)
  return resultFor(resultingBoard, seat, captured, opponentBagCount)
}

/** The full legality check and effect for a single Strike action: capture `target` from `striker` without moving. */
export function applyStrike(args: {
  board: readonly PlacedTileDto[]
  seat: Seat
  striker: BoardSquare
  target: BoardSquare
  opponentBagCount?: number
}): MoveResult {
  const { board, seat, striker, target, opponentBagCount = 0 } = args

  const strikerTile = pieceAt(board, striker)
  if (strikerTile === null) return { kind: 'rejected', reason: 'no-tile' }
  if (strikerTile.owner !== seat) return { kind: 'rejected', reason: 'not-your-tile' }

  const legalTargets = strikeTargets(board, seat, striker)
  if (!legalTargets.some((sq) => sameSquare(sq, target))) return { kind: 'rejected', reason: 'illegal-strike' }

  const resultingBoard = simulateStrike(board, seat, striker, target)
  if (isDukeInCheck(resultingBoard, seat)) {
    return { kind: 'rejected', reason: 'duke-would-be-captured' }
  }

  const captured = pieceAt(board, target)
  return resultFor(resultingBoard, seat, captured, opponentBagCount)
}

/**
 * The single dispatcher over every turn action kind — the shape
 * `server/duke-api.store.ts` calls. `draw` is handled by the caller before
 * reaching here (it needs the bag, which lives outside this pure module);
 * `applyTurn` only covers the three board-legality actions.
 */
export function applyTurn(args: {
  board: readonly PlacedTileDto[]
  seat: Seat
  action:
    | { type: 'move'; from: BoardSquare; to: BoardSquare }
    | { type: 'command'; commander: BoardSquare; from: BoardSquare; to: BoardSquare }
    | { type: 'strike'; striker: BoardSquare; target: BoardSquare }
  opponentBagCount?: number
}): MoveResult {
  const { board, seat, action, opponentBagCount } = args
  if (action.type === 'move') return applyMove({ board, seat, from: action.from, to: action.to, opponentBagCount })
  if (action.type === 'command')
    return applyCommand({
      board,
      seat,
      commander: action.commander,
      from: action.from,
      to: action.to,
      opponentBagCount,
    })
  return applyStrike({ board, seat, striker: action.striker, target: action.target, opponentBagCount })
}
